import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { StatusBadge } from '../../_shared/components/StatusBadge';
import { MethodPill } from '../../_shared/components/MethodPill';
import { GatewayConfirmDialog } from '../../_shared/components/GatewayConfirmDialog';
import { ComingSoonState } from '../../_shared/components/ComingSoonState';
import { RouteFormDialog } from './RouteFormDialog';
import { useRoutesQuery, usePatchRoute, useDeleteRoute, useRoutesSummary } from '../hooks/useRoutes';
import { useHasRole } from '../../../_shared/hooks/useCurrentUser';
import type { RouteRow, RouteFilters } from '../types/route';

// Skeleton loader for table
function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: '48px',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

export function RoutesListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<RouteFilters>({});
  const [page, setPage] = useState(0);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<RouteRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RouteRow | null>(null);
  // #441 (UAT 2026-05-13): '라우트 등록이란?' 도움말 패널 접힘 상태 (기본 펼침=true → 사용자 학습 후 접기).
  const [helpOpen, setHelpOpen] = useState(true);

  // #416: EXEC sandbox detection — ROLE_EXEC + !ROLE_ADMIN
  const isExec = useHasRole('ROLE_EXEC');
  const isAdmin = useHasRole('ROLE_ADMIN');
  const isExecSandbox = isExec && !isAdmin;

  // #447 (UAT 2026-05-14): 삭제 토스트 — 다이얼로그 닫힘 후 성공/실패 메시지 노출.
  const [toast, setToast] = useState<{ message: string; kind: 'success' | 'error' } | null>(null);

  const { data, isLoading, isError } = useRoutesQuery(filters, page);
  const summary = useRoutesSummary();
  const patchRoute = usePatchRoute();
  const deleteRoute = useDeleteRoute(
    () => {
      const deletedName = deleteTarget?.name ?? '';
      setDeleteTarget(null);
      setToast({ message: t('gateway.routes.toast.deletedVerify', { name: deletedName }), kind: 'success' });
      setTimeout(() => setToast(null), 2500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
      setTimeout(() => setToast(null), 3500);
    },
  );

  const columns: DataTableColumn<RouteRow>[] = [
    {
      key: 'name',
      header: t('gateway.routes.columns.name'),
      width: '180px',
    },
    {
      key: 'paths',
      header: t('gateway.routes.columns.paths'),
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
          {row.paths.join(', ')}
        </span>
      ),
    },
    {
      key: 'methods',
      header: t('gateway.routes.columns.methods'),
      width: '200px',
      render: (row) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)' }}>
          {(row.methods ?? []).map((m) => (
            <MethodPill key={m} method={m as 'GET'} size="sm" />
          ))}
        </div>
      ),
    },
    {
      key: 'serviceName',
      header: t('gateway.routes.columns.service'),
      width: '140px',
      render: (row) => <span>{row.serviceName ?? row.serviceId.slice(0, 8)}</span>,
    },
    {
      key: 'enabled',
      header: t('gateway.routes.columns.enabled'),
      width: '100px',
      render: (row) => (
        <StatusBadge status={row.enabled ? 'active' : 'disabled'} size="sm" />
      ),
    },
    {
      key: 'actions',
      header: t('gateway.routes.columns.actions'),
      width: '200px',
      render: (row) => {
        // #440 (UAT 2026-05-13): role 기반 → systemManaged 기반 가드로 좁힘.
        // EXEC 가 systemManaged=true(시드) 라우트에 한해 disabled. 사용자 라우트(systemManaged=false) 는 EXEC 도 활성.
        const lockedForExec = isExecSandbox && row.systemManaged === true;
        const lockTitle = lockedForExec ? t('gateway.routes.systemManaged.tooltip') : undefined;
        return (
          <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
            <button
              type="button"
              aria-label={`${row.name} ${t('gateway.shared.actions.edit')}`}
              disabled={lockedForExec}
              aria-disabled={lockedForExec}
              title={lockTitle}
              onClick={(e) => {
                e.stopPropagation();
                if (!lockedForExec) { setSelectedRoute(row); setDialogMode('edit'); }
              }}
              style={{ ...actionBtnStyle, opacity: lockedForExec ? 0.4 : 1, cursor: lockedForExec ? 'not-allowed' : 'pointer' }}
            >
              {t('gateway.shared.actions.edit')}
            </button>
            <button
              type="button"
              aria-label={`${row.name} ${row.enabled ? t('gateway.shared.actions.disable') : t('gateway.shared.actions.enable')}`}
              disabled={lockedForExec}
              aria-disabled={lockedForExec}
              title={lockTitle}
              onClick={(e) => {
                e.stopPropagation();
                if (!lockedForExec) patchRoute.mutate({ id: row.id, body: { enabled: !row.enabled } });
              }}
              style={{ ...actionBtnStyle, opacity: lockedForExec ? 0.4 : 1, cursor: lockedForExec ? 'not-allowed' : 'pointer' }}
            >
              {row.enabled ? t('gateway.shared.actions.disable') : t('gateway.shared.actions.enable')}
            </button>
            <button
              type="button"
              aria-label={`${row.name} ${t('gateway.shared.actions.delete')}`}
              disabled={lockedForExec}
              aria-disabled={lockedForExec}
              title={lockTitle}
              onClick={(e) => {
                e.stopPropagation();
                if (!lockedForExec) setDeleteTarget(row);
              }}
              style={{ ...actionBtnStyle, color: lockedForExec ? undefined : 'var(--color-danger)', borderColor: lockedForExec ? undefined : 'var(--color-danger)', opacity: lockedForExec ? 0.4 : 1, cursor: lockedForExec ? 'not-allowed' : 'pointer' }}
            >
              {t('gateway.shared.actions.delete')}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('gateway.routes.title')}
        breadcrumb={t('gateway.routes.breadcrumb')}
        actions={
          <button
            type="button"
            onClick={() => { setSelectedRoute(null); setDialogMode('create'); }}
            style={primaryBtnStyle}
          >
            {t('gateway.routes.actions.create')}
          </button>
        }
      />

      {/* #441 (UAT 2026-05-13): '라우트 등록이란?' 도움말 패널 — 접힘/펼침. */}
      <div
        style={{
          margin: 'var(--spacing-2) var(--spacing-6) 0',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          aria-expanded={helpOpen}
          aria-label={helpOpen ? t('gateway.routes.help.closeLabel') : t('gateway.routes.help.openLabel')}
          onClick={() => setHelpOpen((v) => !v)}
          style={{
            width: '100%',
            padding: 'var(--spacing-2) var(--spacing-4)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span aria-hidden="true">&#9432;</span>
            {t('gateway.routes.help.title')}
          </span>
          <span aria-hidden="true">{helpOpen ? '▴' : '▾'}</span>
        </button>
        {helpOpen && (
          <div
            style={{
              padding: '0 var(--spacing-4) var(--spacing-3)',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-sm)',
              lineHeight: 1.6,
            }}
          >
            {t('gateway.routes.help.body')}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div
        role="region"
        aria-label={t('gateway.routes.summary.title')}
        style={{
          display: 'flex',
          gap: 'var(--spacing-4)',
          padding: 'var(--spacing-4) var(--spacing-6)',
          flexWrap: 'wrap',
        }}
      >
        <div style={summaryCardStyle}>
          <span style={summaryLabelStyle}>{t('gateway.routes.summary.totalRoutes')}</span>
          <span style={summaryValueStyle} aria-live="polite">
            {summary.isLoading ? '—' : summary.totalRoutes}
          </span>
        </div>
        <div style={{ ...summaryCardStyle, borderColor: 'var(--color-success)' }}>
          <span style={summaryLabelStyle}>{t('gateway.routes.summary.activeRoutes')}</span>
          <span style={{ ...summaryValueStyle, color: 'var(--color-success)' }} aria-live="polite">
            {summary.isLoading ? '—' : summary.activeRoutes}
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-4) var(--spacing-6)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <select
          aria-label={t('gateway.shared.filter.methodFilter')}
          value={filters.method ?? ''}
          onChange={(e) => setFilters({ ...filters, method: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">{t('gateway.shared.filter.allMethods')}</option>
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        <select
          aria-label={t('gateway.shared.filter.enabledFilter')}
          value={filters.enabled === undefined ? '' : String(filters.enabled)}
          onChange={(e) => {
            const v = e.target.value;
            setFilters({ ...filters, enabled: v === '' ? undefined : v === 'true' });
          }}
          style={selectStyle}
        >
          <option value="">{t('gateway.shared.filter.allStatus')}</option>
          <option value="true">{t('gateway.shared.status.active')}</option>
          <option value="false">{t('gateway.shared.status.disabled')}</option>
        </select>

        <input
          type="search"
          aria-label={t('gateway.routes.filter.search')}
          placeholder={t('gateway.routes.filter.search')}
          value={filters.q ?? ''}
          onChange={(e) => setFilters({ ...filters, q: e.target.value || undefined })}
          style={{ ...selectStyle, flex: 1, minWidth: '200px' }}
        />
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ComingSoonState />
        ) : !data || data.content.length === 0 ? (
          <EmptyState
            title={t('gateway.routes.list.empty')}
            description={t('gateway.routes.list.emptyDesc')}
            action={
              <button
                type="button"
                onClick={() => { setSelectedRoute(null); setDialogMode('create'); }}
                style={primaryBtnStyle}
              >
                {t('gateway.routes.list.emptyAction')}
              </button>
            }
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={data.content}
              rowKey={(row) => row.id}
            />
            {/* Pagination */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 'var(--spacing-4)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <span>
                {t('gateway.shared.pagination.range', {
                  total: data.totalElements,
                  start: data.number * data.size + 1,
                  end: Math.min((data.number + 1) * data.size, data.totalElements),
                })}
              </span>
              <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={data.first}
                  onClick={() => setPage((p) => p - 1)}
                  aria-label={t('gateway.shared.pagination.prevAriaLabel')}
                  style={pageBtnStyle}
                >
                  &laquo; {t('gateway.shared.pagination.prev')}
                </button>
                <span style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>
                  {data.number + 1} / {data.totalPages}
                </span>
                <button
                  type="button"
                  disabled={data.last}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label={t('gateway.shared.pagination.nextAriaLabel')}
                  style={pageBtnStyle}
                >
                  {t('gateway.shared.pagination.next')} &raquo;
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Form Dialog */}
      {dialogMode && (
        <RouteFormDialog
          open={!!dialogMode}
          mode={dialogMode}
          initial={selectedRoute}
          onClose={() => setDialogMode(null)}
          onSaved={() => setDialogMode(null)}
        />
      )}

      {/* Delete Confirm Dialog */}
      {deleteTarget && (
        <GatewayConfirmDialog
          open={!!deleteTarget}
          title={t('gateway.routes.delete.confirm')}
          description={t('gateway.routes.delete.description')}
          destructive
          confirmLabel={t('gateway.shared.actions.delete')}
          // #447 (UAT 2026-05-14): mutate → mutateAsync 로 변경 — confirm 다이얼로그가
          // 요청 완료까지 loading 표시 + 실패 시 throw 로 토스트 트리거.
          // 성공 콜백에서 setDeleteTarget(null) 로 닫힘, 실패 시 다이얼로그 유지 + 에러 토스트.
          onConfirm={async () => {
            try {
              await deleteRoute.mutateAsync(deleteTarget.id);
            } catch {
              /* onError 콜백이 토스트를 노출 */
            }
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* #447 (UAT 2026-05-14): 삭제 결과 토스트 (성공/실패) */}
      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: toast.kind === 'error' ? 'var(--color-danger)' : 'var(--color-success)',
            color: 'var(--color-text-inverse)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 3000,
            maxWidth: '480px',
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
};

const actionBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  background: 'var(--color-surface-background)',
  // #439 (UAT 2026-05-13): 액션 버튼 기본 텍스트는 primary 사용 — 보조 톤은 다크모드 대비가 약함.
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
  // #439 (UAT 2026-05-13): 다크모드에서 native select 가 시스템 검정색을 쓰는 회귀 차단.
  // 명시적으로 text token 을 지정하여 대비 보장 (라이트/다크 모두 정합).
  color: 'var(--color-text-primary)',
};

const pageBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface-background)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
};

const summaryCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  padding: 'var(--spacing-4) var(--spacing-6)',
  background: 'var(--color-surface-background)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-lg)',
  minWidth: '140px',
  flex: '1 1 140px',
  maxWidth: '240px',
};

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
};

const summaryValueStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-2xl)',
  fontWeight: 700,
  color: 'var(--color-brand-primary)',
  lineHeight: 1.2,
};
